// SmsComposeModal — quick SMS compose for anywhere in the app.
//
// Used from ClientRoom's Text button (and reusable elsewhere). Hits
// /api/sms/send-manual with adhoc_phone so it doesn't depend on the
// recipient existing in user_state.leads. Every send goes through the
// server's canDial + CONSENT + SHIELD + quiet-hours gates — this is
// just a nicer entry point than pushing the operator to /leads.
//
// Props:
//   ownerId       — required (used for the endpoint context)
//   toPhone       — required E.164 (or normalize on submit)
//   contactName   — display only
//   onClose       — dismiss the modal
//   onSent        — called after successful send

import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import hq from '../hq/theme';

function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (String(raw).trim().startsWith('+')) return String(raw).trim();
  return `+${digits}`;
}

export default function SmsComposeModal({ toPhone, contactName, initialBody = '', onClose, onSent }) {
  const [body, setBody] = useState(initialBody);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(false);
  // When no toPhone is supplied (a "New Message" from scratch), the recipient
  // is editable here instead of dead-ending the user in a leads list.
  const [recipient, setRecipient] = useState(toPhone || '');
  const recipientLocked = !!toPhone;

  const normalized = normalizePhone(recipient);
  const canSend = body.trim() && normalized && !busy;

  async function submit(e) {
    e.preventDefault();
    if (!canSend) return;
    setErr(null); setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      const r = await fetch('/api/sms/send-manual', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ adhoc_phone: normalized, body: body.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.error) {
        const hint = r.status === 412 ? 'Provision your Twilio number first (Settings → Phone).'
                   : r.status === 402 ? 'Wallet balance low — top up before sending.'
                   : r.status === 429 ? 'Rate-limited — wait a moment and try again.'
                   : '';
        throw new Error((j.error || `send failed ${r.status}`) + (hint ? ` — ${hint}` : ''));
      }
      if (j.dry_run) {
        setErr('SMS_SEND_ENABLED is off in this environment — the message was not actually sent.');
        setBusy(false);
        return;
      }
      setSent(true);
      setBusy(false);
      onSent?.();
      setTimeout(() => onClose?.(), 900);
    } catch (e) {
      setErr(e.message || 'send_failed');
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(4,8,12,0.72)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          width: '100%', maxWidth: 500,
          background: hq.color.panel,
          border: `1px solid ${hq.color.line}`,
          borderRadius: 14, padding: 24,
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: hq.color.amber, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
              Send SMS
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: hq.color.text }}>
              {recipientLocked ? `To ${contactName || normalized || 'client'}` : 'New message'}
            </div>
            {recipientLocked && normalized && (
              <div style={{ fontSize: 12, color: hq.color.textSec, marginTop: 2, fontFamily: 'ui-monospace, monospace' }}>
                {normalized}
              </div>
            )}
          </div>
          <button
            type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: hq.color.textSec, fontSize: 20, cursor: 'pointer' }}
            aria-label="Close"
          >×</button>
        </div>

        {!recipientLocked && (
          <input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="Recipient mobile — e.g. (512) 555-1234"
            inputMode="tel"
            autoFocus
            style={{
              width: '100%', padding: '11px 14px',
              background: 'rgba(255,255,255,0.03)', color: hq.color.text,
              border: `1px solid ${normalized ? hq.color.line : 'rgba(245,166,35,0.40)'}`, borderRadius: 8,
              fontFamily: 'ui-monospace, monospace', fontSize: 14, outline: 'none',
            }}
          />
        )}

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type your message…"
          rows={5}
          maxLength={1600}
          autoFocus={recipientLocked}
          style={{
            width: '100%', padding: '12px 14px',
            background: 'rgba(255,255,255,0.03)', color: hq.color.text,
            border: `1px solid ${hq.color.line}`, borderRadius: 8,
            fontFamily: 'inherit', fontSize: 14, resize: 'vertical',
            outline: 'none', minHeight: 100,
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: hq.color.textSec }}>
          <span>Sends through your Twilio number — CONSENT + quiet hours enforced server-side.</span>
          <span>{body.length}/1600</span>
        </div>

        {err && (
          <div style={{
            padding: 10, borderRadius: 6,
            background: 'rgba(196,88,74,0.14)',
            border: '1px solid rgba(196,88,74,0.42)',
            color: '#f5c6c2', fontSize: 12, lineHeight: 1.4,
          }}>{err}</div>
        )}

        {sent && (
          <div style={{
            padding: 10, borderRadius: 6,
            background: 'rgba(91,163,111,0.14)',
            border: '1px solid rgba(91,163,111,0.42)',
            color: '#a7f3d0', fontSize: 12,
          }}>Sent.</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            type="button" onClick={onClose}
            style={{
              padding: '10px 18px', borderRadius: 8, border: `1px solid ${hq.color.line}`,
              background: 'transparent', color: hq.color.textSec, fontSize: 13, cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            type="submit" disabled={!canSend}
            style={{
              padding: '10px 22px', borderRadius: 8, border: 'none',
              background: canSend ? hq.color.amber : 'rgba(245,166,35,0.3)',
              color: '#0b0d10', fontWeight: 700, fontSize: 13,
              cursor: canSend ? 'pointer' : 'not-allowed',
            }}
          >{busy ? 'Sending…' : 'Send'}</button>
        </div>
      </form>
    </div>
  );
}
